import castErrorToBaseError from '@/functions/castErrorToBaseError';
import config from '@/modules/config/functions/config';
import updateLockStore from '@/modules/core/stores/update-lock';
import PromotionForm from '@/modules/promotions/components/PromotionForm';
import usePromotionFormDefaultValues from '@/modules/promotions/hooks/usePromotionFormDefaultValues';
import usePromotionFormValidationSchema from '@/modules/promotions/hooks/usePromotionFormValidationSchema';
import useCreatePromotionMutation from '@/modules/promotions/mutations/useCreatePromotionMutation';
import type { PromotionCreationFormValues } from '@/modules/promotions/types/PromotionCreationFormValues';
import { Helmet } from '@dr.pogodin/react-helmet';
import { joiResolver } from '@hookform/resolvers/joi';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ErrorPage,
  isEmptyString,
  Skeleton,
  Typography,
  useNotification,
} from '@thisisdmg/tirecloud-patterns/v2';
import { type } from 'arktype';
import { clamp } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import classes from './index.module.css';

export const Route = createFileRoute(
  '/_main-ui/_protected/promotions/duplicate/$id'
)({
  component: PromotionDuplicatePage,
  validateSearch: type({
    s: type.string
      .or(type.number)
      .or(type.undefined)
      .pipe((s) =>
        match(s)
          .with(P.number, (x) => Math.max(0, x))
          .with(P.string, (x) => Math.max(0, ~~x))
          .otherwise(() => 0)
      )
      .default(0),
  }),
});

function PromotionDuplicatePage() {
  const { t, i18n } = useTranslation();
  const createPromotionMutation = useCreatePromotionMutation();
  const notification = useNotification();
  const navigate = useNavigate();
  const id = Route.useParams({ select: (p) => p.id });
  const _stepFromSearch = Route.useSearch({ select: (s) => s.s });
  const stepFromSearch = useMemo(
    () => clamp(_stepFromSearch, 0, 4),
    [_stepFromSearch]
  );

  useEffect(() => {
    updateLockStore.getState().lockUpdate();
    return () => updateLockStore.getState().unlockUpdate();
  }, []);

  const {
    defaultValues,
    promotionData,
    isLoadingPromotion,
    promotionFetchingError,
    refetchPromotion,
  } = usePromotionFormDefaultValues(~~id);

  const promotionCreationFormValuesSchema = usePromotionFormValidationSchema();

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting, isValid, ...formState },
    ...form
  } = useForm<PromotionCreationFormValues>({
    mode: 'all',
    values: { ...defaultValues, status: 'planned' },
    shouldFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(promotionCreationFormValuesSchema, {
      abortEarly: false,
    }),
  });

  const submitForm: SubmitHandler<PromotionCreationFormValues> = useCallback(
    async (value) => {
      return createPromotionMutation
        .mutateAsync(value)
        .then(() => {
          notification.success({
            message: t('common.success'),
            description: t('promotions.promotion_created'),
          });
          navigate({
            to: '/promotions',
            search: {
              ref: 'promotion_duplicate',
            },
          });
        })
        .catch((error) => {
          const e = castErrorToBaseError(error);
          notification.error({
            key: e.id,
            message: `0x${e.id.toString(16).toUpperCase()}`,
            description: t(`errors.gen.${e.path}`, { errorId: e.id }),
          });
        });
    },
    [createPromotionMutation, notification, t, navigate]
  );

  const promotionName = useMemo(() => {
    return (
      promotionData?.data.name[i18n.language] ??
      promotionData?.data.name[config('i18n.fallbackLanguage', '')]
    );
  }, [promotionData, i18n.language]);

  return (
    <>
      {!isEmptyString(promotionName) ? (
        <Helmet titleTemplate={`%s - ${t('promotions.duplicate_promotion')}`}>
          <title>{promotionName}</title>
        </Helmet>
      ) : (
        <Helmet>
          <title>{t('promotions.duplicate_promotion')}</title>
        </Helmet>
      )}
      <div className={classes.promotion_duplicate_page}>
        {isLoadingPromotion ? (
          <>
            <Skeleton active />
          </>
        ) : promotionFetchingError ? (
          <div className={classes.error}>
            <ErrorPage
              code={[5, 0, 0]}
              title={t('common.oops')}
              description={t(`errors.gen.${promotionFetchingError.path}`, {
                errorId: promotionFetchingError.id,
              })}
              goBackButton={{
                label: t('common.reload'),
                onClick: () => {
                  refetchPromotion();
                },
              }}
            />
          </div>
        ) : (
          <>
            <div className={classes.header}>
              <Typography variant="hero">
                {t('promotions.duplicate_promotion')}
              </Typography>
            </div>
            <FormProvider
              {...{
                ...form,
                control,
                watch,
                setValue,
                handleSubmit,
                trigger,
                formState: { ...formState, errors, isSubmitting, isValid },
                errors,
              }}
            >
              <PromotionForm
                onSubmit={submitForm}
                isLoadingInitialValues={isLoadingPromotion}
                currentStep={stepFromSearch}
                setCurrentStep={(fn) => {
                  navigate({
                    to: '/promotions/duplicate/$id',
                    params: { id },
                    search: (prev) => ({ ...prev, s: fn(stepFromSearch) }),
                    replace: true,
                  });
                }}
              />
            </FormProvider>
          </>
        )}
      </div>
    </>
  );
}
